/**
 * Creates a function to attach impression handlers to an element. To conform with the Client Event
 * Schema specifications, the handler will by default ensure that an impression event fires only if
 * an element is at least 50% visible in the viewport for at least 1 second.
 *
 * @param {Function} handler - A function handler to (presumably) send a client event on click
 * @param {Number} options.threshold - % of element to be in viewport before eligible for impression
 * @param {Number} options.visibility_time - Minimum amount of time an element should be visible (within threshold) before sending event.
 * @return {Function} - A function to attach the handler to a DOM element
 */
var impression = (function (handler) {
  let {
    threshold = 0.5,
    visibility_time = 1000
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  /**
   * When executing on the server, return a no-op function.
   */
  if (typeof window === 'undefined') {
    return () => {};
  }

  // A map of elements and their associated handlers.
  const elementHandlers = new Map();

  // A map of elements and their associated `setTimeout` ID. Use to make sure element has been in view for the minimum specified time (visibility_time)
  const elementVisibilityTimeouts = new Map();
  const onObserve = (items, observer) => {
    items.forEach(item => {
      // We're not interested in this element
      if (!elementHandlers.has(item.target)) {
        return;
      }

      // if we have a timeout for this element and the element falls out of view, reset the timeout
      if (!item.isIntersecting) {
        clearTimeout(elementVisibilityTimeouts.get(item.target));
        elementVisibilityTimeouts.delete(item.target);
        return;
      }

      // If the item is still visible with a timer running or not visible, do nothing
      if (elementVisibilityTimeouts.has(item.target) || item.intersectionRatio < threshold) {
        return;
      }

      // Item has come into view, let's set a timer to fire the event as long as it stays in view
      const timeout = setTimeout(() => {
        elementHandlers.get(item.target).call(null);
        observer.unobserve(item.target);
        elementVisibilityTimeouts.delete(item.target);
      }, visibility_time);
      elementVisibilityTimeouts.set(item.target, timeout);
    });
  };
  const observerInstance = new IntersectionObserver(onObserve, {
    threshold: [0, threshold, 1]
  });

  /**
   * This function takes an element and creates a new handler (with additional_args in closure).
   * The element and new handler are added to the `elementHandlers` map and to the
   * `IntersectionObserver` to be observed.
   *
   * @param {HTMLElement} element - DOM element to observe
   * @param {...Any} additional_args - An unspecified number of arguments to pass back to the provided event handler
   * @return {Function} - A function to remove all listeners, observers and timeouts associated with an element
   */
  const addImpressionListener = function (element) {
    for (var _len = arguments.length, additional_args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      additional_args[_key - 1] = arguments[_key];
    }
    if (!elementHandlers.has(element)) {
      elementHandlers.set(element, () => handler(...additional_args));
      observerInstance.observe(element);
    }
    return () => {
      elementHandlers.delete(element);
      observerInstance.unobserve(element);
      if (elementVisibilityTimeouts.has(element)) {
        clearTimeout(elementVisibilityTimeouts.get(element));
        elementVisibilityTimeouts.delete(element);
      }
    };
  };
  return function (element) {
    for (var _len2 = arguments.length, additional_args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      additional_args[_key2 - 1] = arguments[_key2];
    }
    return addImpressionListener(element, ...additional_args);
  };
});

export default impression;
